import React, { Fragment, useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Alert,
  Button,
} from "reactstrap";

import DataTable from "react-data-table-component";

import Spinner from "../../Common/Spinner";
import NoDataFound from "../../Common/NoDataFound";

import {
  customStyles,
} from "../../../Constant";

import {
  formatDate,
} from "../../Common/CommonFunction";

import AxiosInstance from "../../../services/AxiosInstance";

const PlanUsageLogs = () => {

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasActivePlan, setHasActivePlan] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState("DESC");

  /**
   * Format Module Name
   */
  const formatModuleName = (moduleName) => {

    if (!moduleName) return "-";

    return moduleName
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  };

  /**
   * Fetch Logs & Active Plan Status
   */
  const fetchLogs = async () => {

    try {

      setLoading(true);

      console.log("Fetching logs...");

      const response = await AxiosInstance().get(
        `/api/usage-logs`,
        {
          params: {
            page: currentPage,
            per_page: perPage,
            sort_column: sortColumn,
            sort_direction: sortDirection,
          },
        }
      );

      console.log("API RESPONSE => ", response);

      if (response?.data?.status) {

        const dataList = response?.data?.data || [];
        setLogs(dataList);
        setTotalRows(
          response?.data?.total_count ||
          response?.data?.count ||
          0
        );

        // Determine active plan status from API response or fallback profile API
        let isPlanActive =
          typeof response?.data?.has_active_plan === "boolean"
            ? response?.data?.has_active_plan
            : typeof response?.data?.is_active_plan === "boolean"
            ? response?.data?.is_active_plan
            : typeof response?.data?.has_plan === "boolean"
            ? response?.data?.has_plan
            : response?.data?.active_plan
            ? true
            : false;

        if (!isPlanActive) {
          try {
            const profileRes = await AxiosInstance().post(
              "/recruiter/fetch_recruiter_profile_details"
            );
            if (profileRes?.data?.status && profileRes?.data?.data?.has_active_plan) {
              isPlanActive = true;
            }
          } catch (err) {
            console.log("Error checking profile active plan fallback:", err);
          }
        }

        setHasActivePlan(isPlanActive);

      } else {

        setLogs([]);
        setTotalRows(0);
        setHasActivePlan(false);

      }

    } catch (error) {

      console.log("API ERROR => ", error);

      setLogs([]);
      setTotalRows(0);
      setHasActivePlan(false);

    } finally {

      setLoading(false);

    }
  };

  /**
   * Initial Load + Pagination + Sorting
   */
  useEffect(() => {

    fetchLogs();

  }, [currentPage, perPage, sortColumn, sortDirection]);

  /**
   * Pagination
   */
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  /**
   * Per Page Change
   */
  const handlePerRowsChange = (newPerPage, page) => {

    setPerPage(newPerPage);
    setCurrentPage(page);

  };

  /**
   * Sorting
   */
  const handleSort = (column, direction) => {

    setSortColumn(column.sortField || "id");
    setSortDirection(direction.toUpperCase());

  };

  /**
   * Redirect to Plan URL
   */
  const handleRedirectToPlan = () => {
    window.location.href = "/plan.php";
  };

  /**
   * Table Columns
   */
  const columns = [
    {
      name: "#",
      width: "70px",
      cell: (row, index) =>
        (currentPage - 1) * perPage + index + 1,
    },

    {
      name: "Plan",
      selector: (row) => row.plan_name || "-",
      sortable: true,
      sortField: "plan_name",
      center: true,
      cell: (row) => (
        <Badge color="primary" pill>
          {row.plan_name || "N/A"}
        </Badge>
      ),
    },

    {
      name: "Module",
      selector: (row) => row.module_name || "-",
      sortable: true,
      sortField: "module_name",
      cell: (row) => (
        <span className="fw-bold">
          {formatModuleName(row.module_name)}
        </span>
      ),
    },

    {
      name: "Usage Count",
      selector: (row) => row.usage_count || 0,
      sortable: true,
      sortField: "usage_count",
      center: true,
    },

    {
      name: "Used On",
      selector: (row) => row.created_at,
      sortable: true,
      sortField: "created_at",
      center: true,
      cell: (row) =>
        row.created_at
          ? formatDate(row.created_at)
          : "-",
    },
  ];

  return (
    <Fragment>

      <Row>

        <Col xs="12">

          <Card>

            <CardHeader className="py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h5 className="mb-0">
                Plan Usage Logs
              </h5>
              <div>
                {hasActivePlan ? (
                  <Button
                    color="primary"
                    className="d-inline-flex align-items-center gap-1"
                    onClick={handleRedirectToPlan}
                  >
                    <i className="fa fa-arrow-up me-1"></i> Upgrade Plan
                  </Button>
                ) : (
                  <Button
                    color="success"
                    className="d-inline-flex align-items-center gap-1"
                    onClick={handleRedirectToPlan}
                  >
                    <i className="fa fa-shopping-cart me-1"></i> Buy Plan
                  </Button>
                )}
              </div>
            </CardHeader>

            <CardBody className="px-0 py-3">

              {!loading && !hasActivePlan && (
                <Alert
                  color="danger"
                  className="mx-3 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2"
                >
                  <div className="d-flex align-items-center gap-2">
                    <i className="fa fa-exclamation-triangle f-18"></i>
                    <span>
                      <strong>No Active Plan:</strong> There is no active plan associated with your account.
                    </span>
                  </div>
                  <Button
                    color="danger"
                    size="sm"
                    className="text-white fw-bold"
                    onClick={handleRedirectToPlan}
                  >
                    Buy Plan
                  </Button>
                </Alert>
              )}

              <DataTable
                customStyles={customStyles}
                data={logs}
                columns={columns}
                striped
                responsive
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={perPage}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                progressPending={loading}
                progressComponent={<Spinner />}
                noDataComponent={
                  <NoDataFound message="No usage logs found" />
                }
                onSort={handleSort}
                persistTableHead
              />

            </CardBody>

          </Card>

        </Col>

      </Row>

    </Fragment>
  );
};

export default PlanUsageLogs;